import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { History, LineChart as LineIcon } from "lucide-react";

const METRICS = [
  { key: "resting_hr", label: "Batimentos (FC repouso)", unit: "bpm", color: "hsl(var(--destructive))" },
  { key: "weight", label: "Peso", unit: "kg", color: "hsl(var(--primary))" },
  { key: "bp_sys", label: "PA sistólica", unit: "mmHg", color: "hsl(var(--destructive))" },
  { key: "bp_dia", label: "PA diastólica", unit: "mmHg", color: "hsl(var(--chart-3))" },
  { key: "sleep_hours", label: "Sono", unit: "h", color: "hsl(var(--chart-5))" },
  { key: "hrv", label: "HRV", unit: "ms", color: "hsl(var(--accent-ai))" },
  { key: "spo2", label: "SpO2", unit: "%", color: "hsl(var(--chart-3))" },
  { key: "steps", label: "Passos", unit: "", color: "hsl(var(--chart-2))" },
];

export default function HistoricoTab({ patientId }) {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get(`/patients/${patientId}/wearables`).then((r) => setRows(r.data));
  }, [patientId]);

  // mais recente primeiro
  const sorted = useMemo(
    () => [...rows].sort((a, b) => (b.date || "").localeCompare(a.date || "")),
    [rows]
  );

  const seriesByMetric = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 30);
    const cutStr = cutoff.toISOString().slice(0, 10);
    const recent = rows.filter((r) => (r.date || "") >= cutStr);
    const out = {};
    for (const mt of METRICS) {
      const pts = recent
        .filter((r) => r[mt.key] != null)
        .sort((a, b) => (a.date || "").localeCompare(b.date || ""))
        .map((r) => ({ date: r.date, valor: r[mt.key] }));
      if (pts.length) out[mt.key] = pts;
    }
    return out;
  }, [rows]);

  const fmt = (v) => (v == null || v === "" ? "—" : v);

  return (
    <div className="space-y-8" data-testid="historico-tab">
      <div>
        <h3 className="font-heading text-xl font-medium flex items-center gap-2">
          <History className="w-5 h-5 text-ai" /> Histórico de saúde
        </h3>
        <p className="text-sm text-muted-foreground">Todos os dados registrados, do mais recente ao mais antigo.</p>
      </div>

      <div>
        <h4 className="font-heading text-lg font-medium flex items-center gap-2 mb-4">
          <LineIcon className="w-4 h-4 text-ai" /> Evolução (últimos 30 dias) — todos os indicadores
        </h4>
        {Object.keys(seriesByMetric).length === 0 ? (
          <Card className="p-10 text-center text-muted-foreground border-dashed">
            Sem dados nos últimos 30 dias.
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="historico-panel">
            {METRICS.filter((mt) => seriesByMetric[mt.key]).map((mt) => {
              const pts = seriesByMetric[mt.key];
              const last = pts[pts.length - 1];
              return (
                <Card key={mt.key} className="p-4 border-border shadow-none" data-testid={`historico-chart-${mt.key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="text-sm font-medium">{mt.label}</h5>
                    <span className="font-mono-data text-sm" style={{ color: mt.color }}>{last.valor} {mt.unit}</span>
                  </div>
                  <ResponsiveContainer width="100%" height={150}>
                    <LineChart data={pts} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} width={32} />
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                        formatter={(v) => [`${v} ${mt.unit}`, mt.label]} />
                      <Line type="monotone" dataKey="valor" name={mt.label} stroke={mt.color} strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Card className="p-0 border-border shadow-none overflow-hidden">
        {sorted.length === 0 ? (
          <p className="text-sm text-muted-foreground p-10 text-center">Nenhum dado de saúde registrado ainda.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Fonte</TableHead>
                  <TableHead className="text-right">Peso (kg)</TableHead>
                  <TableHead className="text-right">FC (bpm)</TableHead>
                  <TableHead className="text-right">PA (mmHg)</TableHead>
                  <TableHead className="text-right">HRV (ms)</TableHead>
                  <TableHead className="text-right">Sono (h)</TableHead>
                  <TableHead className="text-right">SpO2 (%)</TableHead>
                  <TableHead className="text-right">Passos</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((r) => (
                  <TableRow key={r.id} data-testid={`historico-row-${r.id}`}>
                    <TableCell className="font-medium">{r.date}</TableCell>
                    <TableCell className="text-muted-foreground">{r.source}</TableCell>
                    <TableCell className="text-right font-mono-data">{fmt(r.weight)}</TableCell>
                    <TableCell className="text-right font-mono-data">{fmt(r.resting_hr)}</TableCell>
                    <TableCell className="text-right font-mono-data">{r.bp_sys && r.bp_dia ? `${r.bp_sys}/${r.bp_dia}` : "—"}</TableCell>
                    <TableCell className="text-right font-mono-data">{fmt(r.hrv)}</TableCell>
                    <TableCell className="text-right font-mono-data">{fmt(r.sleep_hours)}</TableCell>
                    <TableCell className="text-right font-mono-data">{fmt(r.spo2)}</TableCell>
                    <TableCell className="text-right font-mono-data">{fmt(r.steps)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
